/**
 * CreditHistoryModal — full ledger history with tool filter + CSV export.
 *
 * Opens from AICreditsWidget's "View history" link. Pulls from
 * GET /api/me/ai-credits/history (with ?tool_slug filter). Provides:
 *   • Filter dropdown of tools the user has actually used.
 *   • Period selector (defaults to current month).
 *   • CSV download of the filtered rows.
 *   • Chronological table with cost, tool, before→after balance, and
 *     when the row is a grant (negative cost), a "+X" pill instead.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Download, Loader2 } from 'lucide-react';

const TOOL_LABEL = (slug) => (slug || '').replace(/[._]/g, ' ');

function currentPeriod() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

export default function CreditHistoryModal({ open, onOpenChange }) {
  const { API } = useContext(AuthContext);
  const [rows, setRows]           = useState([]);
  const [toolsUsed, setToolsUsed] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [toolFilter, setToolFilter] = useState('all');
  const [period, setPeriod]       = useState(currentPeriod());
  const [purchasesOnly, setPurchasesOnly] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = { limit: 500 };
      // "Purchases only" is the more specific intent — when on it
      // overrides tool_slug so members get their expense-report
      // friendly view.
      if (purchasesOnly) {
        params.kind = 'purchases';
      } else if (toolFilter && toolFilter !== 'all') {
        params.tool_slug = toolFilter;
      }
      if (period && period !== 'all')          params.period = period;
      const { data } = await axios.get(`${API}/me/ai-credits/history`, { params });
      setRows(data?.rows || []);
      setToolsUsed(data?.tools_used || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [API, open, toolFilter, period, purchasesOnly]);

  useEffect(() => { load(); }, [load]);

  // Build the periods dropdown from distinct months present in rows.
  const periods = useMemo(() => {
    const p = new Set(rows.map((r) => r.period).filter(Boolean));
    p.add(currentPeriod());
    return Array.from(p).sort().reverse();
  }, [rows]);

  const totalCost = rows.reduce((s, r) => s + Math.max(0, r.cost || 0), 0);
  const totalGrants = rows.reduce((s, r) => s + Math.max(0, -(r.cost || 0)), 0);

  const exportCSV = () => {
    const headers = ['when', 'tool', 'cost_credits', 'balance_before', 'balance_after', 'reason'];
    const lines = [headers.join(',')];
    for (const r of rows) {
      const cells = [
        r.at || '',
        r.tool_slug || '',
        r.cost != null ? String(r.cost) : '',
        r.before != null ? String(r.before) : '',
        r.after != null ? String(r.after) : '',
        (r.reason || '').replace(/"/g, '""'),
      ].map((c) => `"${c}"`);
      lines.push(cells.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-credits-${period}${toolFilter && toolFilter !== 'all' ? `-${toolFilter}` : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="credit-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> AI credit history
          </DialogTitle>
          <DialogDescription>
            Every AI activation, ordered newest-first. Filter by tool, jump to a past month, or export CSV.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-8 text-xs px-2 rounded-md border border-input bg-background"
            data-testid="credit-history-period"
          >
            {periods.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            <option value="all">All months</option>
          </select>

          <select
            value={toolFilter}
            onChange={(e) => setToolFilter(e.target.value)}
            className="h-8 text-xs px-2 rounded-md border border-input bg-background disabled:opacity-50"
            data-testid="credit-history-tool-filter"
            disabled={purchasesOnly}
          >
            <option value="all">All tools</option>
            {toolsUsed.map((t) => (
              <option key={t} value={t}>{TOOL_LABEL(t)}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setPurchasesOnly((v) => !v)}
            className={`h-8 text-xs px-3 rounded-full border font-bold uppercase tracking-widest transition-colors ${purchasesOnly ? 'bg-amber-500 border-amber-500 text-navy' : 'border-input text-muted-foreground hover:text-foreground'}`}
            data-testid="credit-history-purchases-only"
            title="Only show credits you bought — useful for expense reports"
          >
            Purchases only
          </button>

          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={exportCSV}
            disabled={rows.length === 0}
            data-testid="credit-history-export"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
        </div>

        <div className="text-xs text-muted-foreground mb-2">
          {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}
          {totalCost > 0 && <> · <b>{totalCost}</b> credits used</>}
          {totalGrants > 0 && <> · <b className="text-emerald-600">+{totalGrants}</b> granted</>}
        </div>

        <div className="flex-1 overflow-y-auto border border-border rounded-md">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No activity for this filter.</div>
          ) : (
            <table className="w-full text-xs" data-testid="credit-history-table">
              <thead className="border-b border-border bg-slate-50 dark:bg-slate-900 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2">When</th>
                  <th className="px-2 py-2">Tool</th>
                  <th className="px-2 py-2 text-right">Cost</th>
                  <th className="px-2 py-2 text-right">Balance after</th>
                  <th className="px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const grant = (r.cost || 0) < 0;
                  return (
                    <tr key={r.id} className="border-b border-border/40" data-testid={`credit-history-row-${r.id}`}>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {r.at ? new Date(r.at).toLocaleString() : '—'}
                      </td>
                      <td className="px-2 py-1.5 font-mono">{TOOL_LABEL(r.tool_slug)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">
                        {grant ? (
                          <span className="text-emerald-600 font-bold">+{Math.abs(r.cost)}</span>
                        ) : (
                          <span>{r.cost}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                        {r.after != null ? r.after : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground truncate max-w-xs">{r.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
